import SquareIcon from "@mui/icons-material/Square";
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Checkbox,
  ListItem,
  TextField,
  TypographyProps,
} from "@mui/material";
import lodash from "lodash";
import { ReactNode } from "react";
import { Option } from "../constants/common";
import {
  CHART_DIMENSIONS,
  CHART_TYPOGRAPHY,
} from "./app/provider/patient/chart/tokens";
import Flex, { FlexProps } from "./Flex";
import { inputLabel } from "./MaskInput";
import Text from "./Text";

type Value = string | null;
type OnSearch = (
  e: React.SyntheticEvent,
  v: string,
  reason: AutocompleteInputChangeReason
) => void;

export interface FilterSelectProps {
  options?: Option[];
  label?: string;
  flexProps?: FlexProps;
  typographyProps?: TypographyProps;
  placeholder?: string;
  optionWithCheckbox?: boolean;
  disableCloseOnSelect?: boolean;
  onChange?: (value: Value) => void;
  endIcon?: ReactNode;
  value?: Value;
  clearable?: boolean;
  required?: boolean;
  error?: string | boolean;
  withStatusBox?: boolean;
  onSearch?: OnSearch;
  readOnly?: boolean;
  handleBlur?: any;
  name?: string;
  fullHeight?: boolean;
  enableFilterOption?: boolean;
  gutterTop?: boolean;
  renderCustomLabel?: (opt: Option) => ReactNode;
  disableSortOptions?: boolean;
}

export const FilterSelect: React.FC<FilterSelectProps> = ({
  name,
  label,
  flexProps,
  placeholder = "Select",
  options,
  onChange,
  value,
  optionWithCheckbox = false,
  disableCloseOnSelect = false,
  clearable = true,
  endIcon,
  required,
  error,
  onSearch,
  withStatusBox = false,
  readOnly = false,
  handleBlur,
  // fullHeight,
  gutterTop,
  enableFilterOption,
  renderCustomLabel,
  disableSortOptions,
}) => {
  const handleInputChange = (e: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      onSearch?.(e, newInputValue, reason);
    }
  };

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        height={CHART_DIMENSIONS.controlHeight + "!important"}
      >
        {/* {label && (
        <Typography mb={"10px"} sx={DEFAULT_FONTSIZE}>
          {label}
          {required && (
            <Typography fontSize={"13px"} component={"span"} color={"error"}>
              {` *`}
            </Typography>
          )}
        </Typography>
      )} */}
        <Autocomplete
          style={{
            marginTop: gutterTop ? "30px" : undefined,
          }}
          onBlur={handleBlur}
          disabled={readOnly}
          options={
            (disableSortOptions
              ? options?.filter((a) => a?.value !== value)
              : options?.sort((a, b) => a?.label?.localeCompare(b?.label))
            )?.filter((a) => a?.value !== value) ?? []
          }
          fullWidth
          disableClearable={!clearable}
          disableCloseOnSelect={disableCloseOnSelect}
          getOptionLabel={(option) => option.label}
          popupIcon={endIcon}
          onInputChange={handleInputChange}
          isOptionEqualToValue={lodash.isEqual}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.preventDefault();
          }}
          filterOptions={enableFilterOption ? (opt) => opt : undefined}
          // Todo need to optimize this (change backend accept value)
          value={options?.find((opt) => opt?.value === value) ?? null}
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              height: CHART_DIMENSIONS.controlHeight,
              minHeight: CHART_DIMENSIONS.controlHeight,
              padding: "0 8px !important",
              display: "flex",
              alignItems: "center",
            },

            "& .MuiOutlinedInput-input": {
              height: "100% !important",
              padding: "0 !important",
              display: "flex",
              alignItems: "center",
              boxSizing: "border-box",
            },

            "& .MuiInputBase-root": {
              height: CHART_DIMENSIONS.controlHeight,
            },

            "& .MuiFormControl-root": {
              height: CHART_DIMENSIONS.controlHeight,
            },
          }}
          onChange={(_, value) => onChange?.(value?.value ?? null)}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            return (
              <ListItem
                key={key}
                {...optionProps}
                sx={{
                  fontSize: CHART_TYPOGRAPHY.body5.fontSize,
                  height: CHART_DIMENSIONS.controlHeight,
                }}
              >
                {optionWithCheckbox && <Checkbox checked={selected} />}
                {withStatusBox && (
                  <SquareIcon
                    sx={{
                      backgroundColor: option.color,
                      color: option.color,
                      marginRight: "5px",
                    }}
                  />
                )}

                {renderCustomLabel ? renderCustomLabel(option) : option.label}
              </ListItem>
            );
          }}
          renderInput={(params: any) => (
            <TextField
              label={inputLabel(label, required)}
              onKeyDown={(event) => {
                if (event.key === "Enter") event.preventDefault();
              }}
              name={name}
              {...params}
              error={!!error && error}
              placeholder={placeholder}
              slotProps={{
                ...params.slotProps,
                inputLabel: { shrink: true },
              }}
            />
          )}
        />
        {!!error && (
          <Text fontSize={"13px"} variant="caption" color="error">
            {error}
          </Text>
        )}
      </Flex>
    </form>
  );
};

export default FilterSelect;
